import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { EventResponse, Paginated, Category, Venue, RegistrationOption } from "@/types";

export function useOrganizerEvents(filters: {
  status?: string;
  search?: string;
  categoryId?: string;
  hidePast?: boolean;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["organizer-events", filters],
    queryFn: () => {
      const params: Record<string, string> = {
        status: filters.status || "all",
        limit: String(filters.limit ?? 25),
      };
      if (filters.search) params.search = filters.search;
      if (filters.categoryId) params.category = filters.categoryId;
      if (filters.hidePast) params.hidePast = "true";
      if (filters.cursor) params.cursor = filters.cursor;
      return api.get<Paginated<EventResponse>>("/organizer/events", params);
    },
  });
}

export function useOrganizerCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      eventDescription: string;
      date: string;
      endDate: string | null;
      isAllDay: boolean;
      venueId: string;
      categoryId: string;
      detailsUrl: string;
      registrationOptions: RegistrationOption[];
      registrationNotes: string;
      source: string;
      sourceUrl: string;
    }) => api.post<EventResponse>("/organizer/events", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizer-events"] }),
  });
}

export function useOrganizerUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        title: string;
        eventDescription: string;
        date: string;
        endDate: string | null;
        isAllDay: boolean;
        venueId: string;
        categoryId: string;
        detailsUrl: string;
        registrationOptions: RegistrationOption[];
        registrationNotes: string;
        source: string;
        sourceUrl: string;
      };
    }) => api.put<EventResponse>(`/organizer/events/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizer-events"] }),
  });
}

export function useOrganizerSubmitEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put<EventResponse>(`/organizer/events/${id}/submit`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizer-events"] }),
  });
}

export function useOrganizerWithdrawEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put<EventResponse>(`/organizer/events/${id}/withdraw`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizer-events"] }),
  });
}

export function useOrganizerDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/organizer/events/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizer-events"] }),
  });
}

export function useOrganizerVenues() {
  return useQuery({
    queryKey: ["organizer-venues"],
    queryFn: () => api.get<Venue[]>("/organizer/venues"),
  });
}

export function useOrganizerCategories() {
  return useQuery({
    queryKey: ["organizer-categories"],
    queryFn: () => api.get<Category[]>("/organizer/categories"),
  });
}
